import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories, sites } from '@/mocks/navigation';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState('全部');
  const [searchTerm, setSearchTerm] = useState('');

  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    setActiveSub('全部');
    setSearchTerm('');
  }, [slug]);

  const filteredSites = useMemo(() => {
    if (!slug || !sites[slug]) return [];
    let list = sites[slug];
    if (activeSub !== '全部') {
      list = list.filter((s) => s.subcategory === activeSub);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [slug, activeSub, searchTerm]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-700 mb-2">分类不存在</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <title>{`${category.name} - 草莓导航`}</title>
      <meta name="description" content={`草莓导航${category.name}分类，${category.description}，精选优质网站资源一站式导航。`} />
      <meta name="keywords" content={`${category.name},网站导航,${category.name}推荐,网址导航`} />
      <link rel="canonical" href={`https://strawberry-nav.com/category/${slug}`} />

      <SEOSchema
        type="WebPage"
        data={{
          name: `${category.name} - 草莓导航`,
          url: `https://strawberry-nav.com/category/${slug}`,
          description: category.description,
          inLanguage: 'zh-CN',
          isPartOf: { '@id': 'https://strawberry-nav.com/#website' },
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://strawberry-nav.com' },
            { '@type': 'ListItem', position: 2, name: category.name },
          ],
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: category.name }]} />

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="w-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${category.icon} text-primary-600 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
              <div className="flex items-center flex-wrap gap-1.5">
                {category.subcategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSub(sub)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeSub === sub
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>

              <div className="relative flex-1 max-w-xs w-full sm:ml-auto">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="搜索此分类下的网站..."
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                />
              </div>
            </div>

            {filteredSites.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">暂无符合条件的网站</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredSites.map((site) => (
                  <a
                    key={site.id}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer hover:scale-[1.01]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110">
                      <i className={`${site.icon} text-primary-600 text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-semibold text-foreground-900">{site.name}</h3>
                        <i className="ri-arrow-right-up-line text-foreground-400 text-sm opacity-0 group-hover:opacity-100 transition-opacity"></i>
                      </div>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed mb-2">
                        {site.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {site.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-700">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 bg-background-100">
          <div className="w-full text-center">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">浏览更多分类</h2>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
              {categories.filter((c) => c.slug !== slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/60 text-sm text-foreground-700 hover:border-primary-200 hover:text-primary-600 transition-all cursor-pointer"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  <span className="whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}